// src/components/admin/CurveControls.js
import React, { useState } from 'react';

const CurveControls = ({
  selectedJobType,
  setSelectedJobType,
  customerTier,
  setCustomerTier,
  jobTypes,
  isLogScale,
  setIsLogScale,
  hasUnsavedChanges,
  onSave,
  onLoad,
  onReset,
  onExport,
  sampleQuantity,
  setSampleQuantity,
  controlPoints,
  onControlPointsChange
}) => {
  // State for manual point addition
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [newPointQuantity, setNewPointQuantity] = useState('');
  const [newPointMargin, setNewPointMargin] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importError, setImportError] = useState(null);

  // Handle manual point addition
  const handleManualAddPoint = () => {
    const quantity = parseInt(newPointQuantity);
    const margin = parseFloat(newPointMargin);

    // Validation
    if (!quantity || !margin || quantity <= 0 || margin <= 0) {
      alert('Please enter valid positive numbers for both quantity and margin.');
      return;
    }

    if (quantity > 10000) {
      alert('Quantity cannot exceed 10,000.');
      return;
    }

    if (margin > 1000) {
      alert('Margin cannot exceed 1000%.');
      return;
    }

    // Check for duplicate quantity
    const existingPoint = controlPoints.find(p => p.quantity === quantity);
    if (existingPoint) {
      alert(`A control point already exists at quantity ${quantity}.`);
      return;
    }

    // Add the new point
    const newPoint = { quantity, margin };
    const updatedPoints = [...controlPoints, newPoint].sort((a, b) => a.quantity - b.quantity);
    onControlPointsChange(updatedPoints);

    // Clear form
    setNewPointQuantity('');
    setNewPointMargin('');
    setShowManualAdd(false);
  };
  // NEW: Import function (add this with your other functions like handleManualAddPoint)
  const handleImport = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  // Reset any previous errors
  setImportError(null);

  // Check file type
  if (!file.name.endsWith('.json')) {
    setImportError('Please select a JSON file (.json)');
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const importedData = JSON.parse(e.target.result);
      
      // Validate the imported data structure
      if (!importedData.controlPoints || !Array.isArray(importedData.controlPoints)) {
        setImportError('Invalid file format: missing or invalid controlPoints array');
        return;
      }

      // Validate each control point has required fields
      const validControlPoints = importedData.controlPoints.every(point => 
        typeof point.quantity === 'number' && 
        typeof point.margin === 'number' &&
        point.quantity > 0 &&
        point.margin >= 0
      );

      if (!validControlPoints) {
        setImportError('Invalid control points: each point must have positive quantity and non-negative margin');
        return;
      }

      // Optional: Warn if importing from different job type
      if (importedData.jobType && importedData.jobType !== selectedJobType) {
        const proceed = window.confirm(
          `This curve was exported from job type "${importedData.jobType}" but you're currently editing "${selectedJobType}". ` +
          'Do you want to continue importing? This will replace the current curve with the imported control points.'
        );
        if (!proceed) {
          setShowImport(false);
          return;
        }
      } else {
        // Confirm replacement for same job type
        const proceed = window.confirm(
          'This will replace the current curve with the imported control points. ' +
          'Any unsaved changes will be lost. Do you want to continue?'
        );
        if (!proceed) {
          setShowImport(false);
          return;
        }
      }

      // Sort control points by quantity to ensure proper curve generation
      const sortedControlPoints = [...importedData.controlPoints].sort((a, b) => a.quantity - b.quantity);

      // Apply the imported control points
      onControlPointsChange(sortedControlPoints);
      
      // Success feedback
      const importInfo = importedData.metadata ? {
        pointCount: importedData.metadata.pointCount || sortedControlPoints.length,
        exportedAt: importedData.metadata.exportedAt ? new Date(importedData.metadata.exportedAt).toLocaleDateString() : 'Unknown',
        sourceJobType: importedData.jobType || 'Unknown'
      } : {
        pointCount: sortedControlPoints.length,
        exportedAt: 'Unknown',
        sourceJobType: importedData.jobType || 'Unknown'
      };

      alert(
        `Curve imported successfully!\n\n` +
        `Control Points: ${importInfo.pointCount}\n` +
        `Source Job Type: ${importInfo.sourceJobType}\n` +
        `Exported: ${importInfo.exportedAt}`
      );

      // Close import dialog
      setShowImport(false);
      
    } catch (error) {
      console.error('Error parsing imported file:', error);
      setImportError('Invalid JSON file or corrupted data');
    }
  };

  reader.onerror = () => {
    setImportError('Error reading file');
  };

  reader.readAsText(file);
  
  // Clear the file input for next use
  event.target.value = '';
};

  // Quick add points at common quantities
  const handleQuickAdd = (quantity) => {
    // Check for duplicate
    if (controlPoints.find(p => p.quantity === quantity)) {
      alert(`A control point already exists at quantity ${quantity}.`);
      return;
    }

    // Calculate interpolated margin from existing curve
    const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
    let margin = 100; // Default

    // Simple interpolation
    if (quantity <= sortedPoints[0].quantity) {
      margin = sortedPoints[0].margin;
    } else if (quantity >= sortedPoints[sortedPoints.length - 1].quantity) {
      margin = sortedPoints[sortedPoints.length - 1].margin;
    } else {
      // Find surrounding points
      for (let i = 0; i < sortedPoints.length - 1; i++) {
        const p1 = sortedPoints[i];
        const p2 = sortedPoints[i + 1];
        
        if (quantity >= p1.quantity && quantity <= p2.quantity) {
          // Linear interpolation
          const ratio = (quantity - p1.quantity) / (p2.quantity - p1.quantity);
          margin = p1.margin + (p2.margin - p1.margin) * ratio;
          break;
        }
      }
    }

    const newPoint = { quantity, margin: Math.round(margin * 10) / 10 };
    const updatedPoints = [...controlPoints, newPoint].sort((a, b) => a.quantity - b.quantity);
    onControlPointsChange(updatedPoints);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Job Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Type
          </label>
          <select
            value={selectedJobType}
            onChange={(e) => setSelectedJobType(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {jobTypes.map(jobType => (
              <option key={jobType} value={jobType}>
                {jobType.charAt(0).toUpperCase() + jobType.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Customer Tier Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Customer Tier
          </label>
          <select
            value={customerTier}
            onChange={(e) => setCustomerTier(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="retail">Retail</option>
            <option value="trade">Trade</option>
            <option value="wholesale">Wholesale</option>
          </select>
        </div>

        {/* Scale Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Scale Type
          </label>
          <div className="flex">
            <button
              onClick={() => setIsLogScale(false)}
              className={`flex-1 py-2 px-3 text-sm rounded-l-md border ${
                !isLogScale
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Linear
            </button>
            <button
              onClick={() => setIsLogScale(true)}
              className={`flex-1 py-2 px-3 text-sm rounded-r-md border-t border-r border-b ${
                isLogScale
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Log
            </button>
          </div>
        </div>

        {/* Sample Quantity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sample Quantity
          </label>
          <input
            type="number"
            value={sampleQuantity}
            onChange={(e) => setSampleQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="1"
            step="1"
          />
        </div>
      </div>

      {/* Control Point Management Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Control Point Management</h4>
        
        {/* Quick Add Buttons */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Add at Common Quantities
          </label>
          <div className="flex flex-wrap gap-2">
            {[50, 100, 250, 500, 750, 1000, 1500, 2000].map(qty => (
              <button
                key={qty}
                onClick={() => handleQuickAdd(qty)}
                disabled={controlPoints.some(p => p.quantity === qty)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  controlPoints.some(p => p.quantity === qty)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {qty}
              </button>
            ))}
          </div>
        </div>

        {/* Manual Add Toggle */}
        <div className="mb-4">
          <button
            onClick={() => setShowManualAdd(!showManualAdd)}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500"
          >
            {showManualAdd ? 'Cancel Manual Add' : 'Add Specific Point'}
          </button>
        </div>

        {/* Manual Add Form */}
        {showManualAdd && (
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h5 className="text-sm font-medium text-green-800 mb-3">Add Control Point</h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  value={newPointQuantity}
                  onChange={(e) => setNewPointQuantity(e.target.value)}
                  placeholder="e.g. 750"
                  min="1"
                  max="10000"
                  className="w-full p-2 border border-green-300 rounded-md focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">
                  Margin (%)
                </label>
                <input
                  type="number"
                  value={newPointMargin}
                  onChange={(e) => setNewPointMargin(e.target.value)}
                  placeholder="e.g. 150.5"
                  min="0"
                  max="1000"
                  step="0.1"
                  className="w-full p-2 border border-green-300 rounded-md focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
              <div>
                <button
                  onClick={handleManualAddPoint}
                  disabled={!newPointQuantity || !newPointMargin}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
                >
                  Add Point
                </button>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2">
              Enter both quantity and margin percentage. The point will be inserted in the correct position on the curve.
            </p>
          </div>
        )}

        {/* Current Points Summary */}
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <div className="flex justify-between items-center mb-2">
            <h5 className="text-sm font-medium text-gray-700">Current Control Points</h5>
            <span className="text-sm text-gray-600">{controlPoints.length} points</span>
          </div>
          <div className="text-xs text-gray-600">
            Range: {controlPoints.length > 0 && (
              <>
                Qty {Math.min(...controlPoints.map(p => p.quantity))} - {Math.max(...controlPoints.map(p => p.quantity))},
                Margin {Math.min(...controlPoints.map(p => p.margin)).toFixed(1)}% - {Math.max(...controlPoints.map(p => p.margin)).toFixed(1)}%
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex flex-wrap gap-3">
        {/* Save/Load Group */}
        <div className="flex gap-2">
          <button
            onClick={onSave}
            disabled={!hasUnsavedChanges}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              hasUnsavedChanges
                ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Save{hasUnsavedChanges ? ' *' : ''}
          </button>
          
          <button
            onClick={onLoad}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
          >
            Load Saved
          </button>
        </div>

        {/* Reset Button */}
        <button
          onClick={onReset}
          className="px-4 py-2 bg-yellow-600 text-white rounded-md text-sm font-medium hover:bg-yellow-700 focus:ring-2 focus:ring-yellow-500"
        >
          Reset to Original
        </button>

        {/* Export Button */}
        <button
          onClick={onExport}
          className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 focus:ring-2 focus:ring-purple-500"
        >
          Export JSON
        </button>

        {/* Import Button - ADD THIS after the Export button */}
        <button
          onClick={() => setShowImport(true)}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500"
        >
          Import JSON
        </button>

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <div className="flex items-center text-amber-600 ml-auto">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">Unsaved changes</span>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Instructions:</strong> Use the chart's "Add Points" mode to click and add points interactively, 
          or use the controls above for precise point placement. Drag existing points to adjust them. 
          Right-click points on the chart to delete them.
          Higher margins at low quantities maximize profit when digital is most competitive. 
          Lower margins at high quantities help compete with litho pricing.
        </p>
      </div>
      {/* Import Modal - ADD THIS at the end of your JSX, before the final closing div */}
{showImport && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Curve from JSON</h3>
      
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-3">
          Select a JSON file that was previously exported from the curve editor. 
          This will replace your current curve with the imported control points.
        </p>
        
        <input
          type="file"
          accept=".json"
          onChange={handleImport}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
      </div>

      {importError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm text-red-800 font-medium">Import Error</p>
              <p className="text-sm text-red-700">{importError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          onClick={() => {
            setShowImport(false);
            setImportError(null);
          }}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default CurveControls;